/**
 * Judge.me dynamic swap
 *
 * Replaces the apps section (`.m-app-section`, configured in templates/product.json
 * as a single Shopify `@app` block pointing at judge-me-reviews/review_widget)
 * with the section HTML rendered for a new product handle, then re-inits
 * Judge.me's interactive widget.
 *
 * Called by `assets/linked-product-swap.js` after AJAX product swap (master
 * ↔ linked) so the visible Judge.me reviews match the currently displayed
 * product, and the section auto-hides for review-less products.
 *
 * Public API:
 *   window.JudgeMeSwap.update(handle) → Promise
 *
 * Debug logs gated by `window.__linkedSwapDebug` (same flag as linked-product-swap).
 */
(function () {
  "use strict";

  var DEBUG = window.__linkedSwapDebug || false;

  /**
   * Find `.m-app-section` and its parent shopify-section wrapper.
   * Returns { appsSection, sectionId } or null if either missing.
   */
  function findAppsSection() {
    var appsSection = document.querySelector(".m-app-section");
    if (!appsSection) return null;
    var shopifySection = appsSection.closest("[id^='shopify-section-']");
    if (!shopifySection) {
      DEBUG && console.warn("[JudgeMeSwap] no parent shopify-section for .m-app-section");
      return null;
    }
    var sectionId = shopifySection.id.replace(/^shopify-section-/, "");
    return { appsSection: appsSection, sectionId: sectionId };
  }

  /**
   * Detect ACTUAL reviews vs Judge.me empty-state placeholder.
   *
   * Judge.me server-renders the widget shell (jdgm-rev-widg, summary header,
   * "No reviews yet" message) even for zero-review products — so a naive
   * `children.length > 0` check returns true for both. Instead read
   * `.jdgm-rev-widg[data-number-of-reviews]` and fall back to counting
   * `.jdgm-rev` items.
   *
   * Returns { hasReviews, numReviewsAttr, revCount }.
   */
  function detectReviews(jdgWidget) {
    var hasReviews = false;
    var numReviewsAttr = null;
    var revCount = 0;
    if (!jdgWidget) return { hasReviews: hasReviews, numReviewsAttr: numReviewsAttr, revCount: revCount };

    var revWidg = jdgWidget.querySelector(".jdgm-rev-widg");
    if (revWidg) {
      numReviewsAttr = revWidg.getAttribute("data-number-of-reviews");
      if (numReviewsAttr != null) hasReviews = parseInt(numReviewsAttr, 10) > 0;
    }
    revCount = jdgWidget.querySelectorAll(".jdgm-rev").length;
    if (!hasReviews) hasReviews = revCount > 0;

    return { hasReviews: hasReviews, numReviewsAttr: numReviewsAttr, revCount: revCount };
  }

  /**
   * Reveal Judge.me widget after DOM mutation.
   *
   * Approach: re-execute Judge.me's review-widget entry script. Judge.me v3
   * loads its main bundle once globally (sets up `window.jdgm`), then loads
   * per-widget entry scripts (e.g. `review-widget/main.js`) which scan for
   * matching widgets and init them. After AJAX innerHTML swap, our new
   * widget DOM hasn't been touched by any of those entry scripts — they ran
   * once on initial page load and the entry is cached in `_loaderExecuted`.
   *
   * Recipe:
   *   1. Remove `.jdgm-temp-hiding-style` (FOUC guard Judge.me normally
   *      strips post-init). Without this, even a successful re-init may
   *      leave the widget visually empty until the bundle clears it.
   *   2. Strip `DONE_SETUP_WIDGET_CLASS` / `DONE_SETUP_CLASS` from inserted
   *      widgets so the re-loaded bundle treats them as fresh.
   *   3. Re-inject the entry script via `<script src=...>`. URL built from
   *      `widgetPath(data-entry-key)`, where `widgetPath` is a fn exposed
   *      on `window.jdgm` and `data-entry-key` is read from the widget DOM
   *      (e.g. `"review-widget/main.js"`).
   */
  function reinitJudgeMe(scope) {
    var root = scope || document;

    if (!window.jdgm) {
      DEBUG && console.warn("[JudgeMeSwap] window.jdgm not loaded — cannot re-init");
      return;
    }

    DEBUG && console.log("[JudgeMeSwap] re-init constants:",
      "DONE_SETUP_WIDGET_CLASS:", window.jdgm.DONE_SETUP_WIDGET_CLASS,
      "| DONE_SETUP_CLASS:", window.jdgm.DONE_SETUP_CLASS,
      "| _loaderExecuted:", window.jdgm._loaderExecuted);

    // Diagnostic: dump window.jdgm methods so we can identify the right
    // setup API to call. Filtered to functions only — prop keys are noise.
    if (DEBUG) {
      try {
        var jdgmKeys = Object.keys(window.jdgm);
        var jdgmFns = jdgmKeys.filter(function (k) {
          return typeof window.jdgm[k] === "function";
        });
        console.log("[JudgeMeSwap] jdgm methods (" + jdgmFns.length + "):", jdgmFns.join(", "));
        console.log("[JudgeMeSwap] jdgm all keys (" + jdgmKeys.length + "):", jdgmKeys.join(", "));
      } catch (e) {
        console.warn("[JudgeMeSwap] failed dumping jdgm keys:", e);
      }
    }

    // Step 1: drop FOUC-prevention style tags. Search GLOBALLY (not just
    // within root scope) — Judge.me may inject one in document head that
    // persists across AJAX swaps and contains rules hiding our widget.
    var tempStyles = document.querySelectorAll(".jdgm-temp-hiding-style");
    tempStyles.forEach(function (el) { el.remove(); });
    DEBUG && console.log("[JudgeMeSwap] removed", tempStyles.length, ".jdgm-temp-hiding-style elements (global)");

    // Diagnostic: dump all <style> tags with "jdgm" in id/class to see what
    // rules are loaded. Helps spot a hidden FOUC style not caught above.
    if (DEBUG) {
      var allJdgmStyles = document.querySelectorAll("style[class*='jdgm'], style[id*='jdgm']");
      console.log("[JudgeMeSwap] active jdgm <style> tags:", allJdgmStyles.length);
      allJdgmStyles.forEach(function (s, i) {
        var preview = (s.textContent || "").substring(0, 200);
        console.log("  [" + i + "]", s.className || s.id, "content:", preview);
      });
    }

    // Step 1b: inject `!important` override style tag. Diagnostic showed
    // children have innerHTML content + `disp:block overflow:visible
    // maxH:none` yet h:0 scrollH:0 — points to CSS `height: 0 !important`
    // somewhere in Judge.me bundle CSS that inline style can't override.
    // Bundle normally lifts the constraint by adding done-setup-widget
    // class + triggering an animation. We skip the bundle path; force
    // visibility directly via injected !important rules.
    if (!document.getElementById("judgeme-swap-force-style")) {
      var styleTag = document.createElement("style");
      styleTag.id = "judgeme-swap-force-style";
      styleTag.textContent =
        // Critical: `.jdgm-legacy-widget-content` is `display:none` by default
        // — bundle's init flips to block via `_customizeReviewWidget` which we
        // skip (destructive). Force open here.
        ".jdgm-legacy-widget-content { display: block !important; } " +
        // Review widget descendants — height/visibility unlock so server-
        // rendered review content actually renders. Bundle CSS sets some of
        // these collapsed pre-init.
        ".jdgm-rev-widg, .jdgm-rev-widg > *, .jdgm-rev-widg__reviews, " +
        ".jdgm-rev-widg__reviews > *, .jdgm-rev, .jdgm-rev > *, " +
        ".jdgm-rev-widg__header > *, .jdgm-rev-widg__summary, " +
        ".jdgm-rev-widg__summary > *, .jdgm-rev-widg__histogram, " +
        ".jdgm-rev-widg__histogram > * { " +
        "height: auto !important; min-height: 0 !important; " +
        "max-height: none !important; opacity: 1 !important; " +
        "visibility: visible !important; }";
        // NOTE: NOT forcing `.jdgm-prev-badge` visible. Badges have
        // `data-id:null` / `data-number-of-reviews:0` post-swap (master
        // product had no reviews and badge data doesn't carry over). User
        // request: empty = don't render anything. Letting bundle's default
        // hide-empty behavior stand. Showing stars for reviewed linked
        // product requires fetching new product's main section + swapping
        // badge HTML — deferred (separate feature).
      document.head.appendChild(styleTag);
      DEBUG && console.log("[JudgeMeSwap] injected force-visibility style tag");
    }

    // Step 2: strip DONE_SETUP classes so the bundle treats widget as fresh.
    var setupWidgetClass = window.jdgm.DONE_SETUP_WIDGET_CLASS;
    var setupClass = window.jdgm.DONE_SETUP_CLASS;
    try {
      if (setupWidgetClass) {
        root.querySelectorAll("." + setupWidgetClass).forEach(function (el) {
          el.classList.remove(setupWidgetClass);
        });
      }
      if (setupClass && setupClass !== setupWidgetClass) {
        root.querySelectorAll("." + setupClass).forEach(function (el) {
          el.classList.remove(setupClass);
        });
      }
    } catch (e) {
      DEBUG && console.warn("[JudgeMeSwap] stripping setup class threw:", e);
    }

    // Step 3: refresh `jdgm.caches.$revWidgets` + `jdgm.caches.$prevBadges`.
    //
    // Why: per widget_common.js, `eachRevWidgets` (used by initializeWidgets
    // and customize* methods) iterates `jdgm.caches.$revWidgets`. That cache
    // is populated ONCE on document-ready with the initial DOM. After our
    // AJAX innerHTML swap, the cache holds STALE jQuery refs to detached
    // nodes — iterating it does nothing visible. Same story for the badge
    // cache `$prevBadges` (preview badges near price).
    //
    // Fix: re-query the DOM via the jQuery handle exposed on `jdgm.$` and
    // overwrite the caches with fresh references that include our new node.
    if (window.jdgm.caches && typeof window.jdgm.$ === "function") {
      try {
        window.jdgm.caches.$revWidgets = window.jdgm.$(".jdgm-review-widget, [data-widget=\"review\"]");
        DEBUG && console.log("[JudgeMeSwap] refreshed caches.$revWidgets, count:", window.jdgm.caches.$revWidgets.length);
      } catch (e) {
        DEBUG && console.warn("[JudgeMeSwap] $revWidgets cache refresh failed:", e);
      }
      try {
        window.jdgm.caches.$prevBadges = window.jdgm.$(".jdgm-prev-badge");
        DEBUG && console.log("[JudgeMeSwap] refreshed caches.$prevBadges, count:", window.jdgm.caches.$prevBadges.length);
      } catch (e) {
        DEBUG && console.warn("[JudgeMeSwap] $prevBadges cache refresh failed:", e);
      }
    } else {
      DEBUG && console.warn("[JudgeMeSwap] jdgm.caches or jdgm.$ missing — cannot refresh caches");
    }

    // Step 4: reset `_doneSetup` flag (bundle's one-time-init guard) so
    // initializeWidgets actually runs its body instead of short-circuiting.
    // Without reset, prior call left `_doneSetup = true` and the function
    // bails before painting children — matches the "no returned ok" silent
    // pattern observed in test logs.
    try {
      DEBUG && console.log("[JudgeMeSwap] _doneSetup before reset:", window.jdgm._doneSetup);
      window.jdgm._doneSetup = false;
    } catch (e) {
      DEBUG && console.warn("[JudgeMeSwap] _doneSetup reset threw:", e);
    }

    // Step 5: invoke `initializeWidgets` — bundle's proper entry point.
    // Per widget_common.js pseudocode: scans `.jdgm-review-widget:not(.DONE_SETUP_WIDGET_CLASS)`,
    // adds empty header if missing, runs `_customizeReviewWidget` (title,
    // summary, histogram, actions, row stars, sort, pagination, footer),
    // sets up media gallery, renders review form, sets up load-reviews
    // events + form submission, marks DONE_SETUP_WIDGET_CLASS. Then calls
    // `customizeReviews()` internally.
    //
    // Then `customizeBadges()` for preview badges (stars near price) —
    // separate from main widget, must be called explicitly.
    var setupMethods = ["initializeWidgets", "customizeBadges"];
    setupMethods.forEach(function (name) {
      if (typeof window.jdgm[name] === "function") {
        DEBUG && console.log("[JudgeMeSwap] invoking window.jdgm." + name + "()");
        try {
          window.jdgm[name]();
          DEBUG && console.log("[JudgeMeSwap] window.jdgm." + name + "() returned ok");
        } catch (e) {
          DEBUG && console.warn("[JudgeMeSwap] " + name + "() threw:", e);
        }
      } else {
        DEBUG && console.warn("[JudgeMeSwap] window.jdgm." + name + " not a fn");
      }
    });

    // Step 5b REMOVED: `_addEmptyWidgetHeader` + `_customizeReviewWidget`
    // calls were DESTRUCTIVE. Diagnostic showed scrollHeight:0 on header
    // AND body after customize ran, despite server HTML containing the
    // `.jdgm-rev` content. Bundle's customize methods re-render widget from
    // internal templates/state which are unpopulated post-AJAX swap → blank
    // out existing content. Trust server-rendered HTML instead.

    // Step 5c: manually add DONE_SETUP classes. Bundle's `initializeWidgets`
    // normally adds these after painting — diagnostic shows it silently bails
    // and never adds them, leaving widget without `jdgm--done-setup-widget`.
    // Many Judge.me CSS rules gate visibility on this class (e.g.
    // `.jdgm-rev-widg__header { max-height: 0 }` until done-setup-widget
    // class flips it), so adding manually unblocks the paint.
    try {
      if (setupWidgetClass) {
        root.querySelectorAll(".jdgm-review-widget").forEach(function (el) {
          el.classList.add(setupWidgetClass);
        });
      }
      if (setupClass) {
        root.querySelectorAll(".jdgm-review-widget, .jdgm-prev-badge").forEach(function (el) {
          el.classList.add(setupClass);
        });
      }
      DEBUG && console.log("[JudgeMeSwap] force-added DONE_SETUP classes:", setupWidgetClass, "+", setupClass);
    } catch (e) {
      DEBUG && console.warn("[JudgeMeSwap] force-add DONE_SETUP threw:", e);
    }

    // Step 5: explicit force-show. Per widget_common.js, `forceShowHideRevWidget(e, t)`
    // calls `e.toggle(t)` + `e.find('.jdgm-rev-widg').toggle(t)`. jQuery `.toggle(true)`
    // sets `display: ''` (removes inline display:none). Belt-and-suspenders in case
    // bundle's internal state left widget toggled off.
    if (typeof window.jdgm.forceShowHideRevWidget === "function"
        && window.jdgm.caches && window.jdgm.caches.$revWidgets
        && typeof window.jdgm.$ === "function") {
      try {
        window.jdgm.caches.$revWidgets.each(function () {
          window.jdgm.forceShowHideRevWidget(window.jdgm.$(this), true);
        });
        DEBUG && console.log("[JudgeMeSwap] forceShowHideRevWidget(true) called on", window.jdgm.caches.$revWidgets.length, "widget(s)");
      } catch (e) {
        DEBUG && console.warn("[JudgeMeSwap] forceShowHideRevWidget threw:", e);
      }
    }

    // Step 6: brute-force clear any inline `display: none` left on jdgm
    // visibility-controlled elements within our scope. Covers the case
    // where bundle's internal toggle ran on initial load and persists.
    try {
      var clearTargets = root.querySelectorAll(".jdgm-rev-widg, .jdgm-prev-badge, .jdgm-widget, .jdgm-review-widget");
      var clearedCount = 0;
      clearTargets.forEach(function (el) {
        if (el.style.display === "none") {
          el.style.display = "";
          clearedCount++;
        }
      });
      DEBUG && console.log("[JudgeMeSwap] cleared inline display:none on", clearedCount, "of", clearTargets.length, "jdgm elements");
    } catch (e) {
      DEBUG && console.warn("[JudgeMeSwap] inline display clear threw:", e);
    }

    // Step 7: diagnostic — log computed display + class state + child render
    // so we can see whether widget is hidden by inline style, class, CSS rule,
    // or has empty children (init didn't paint).
    if (DEBUG) {
      try {
        var jdgWidg = root.querySelector("#judgeme_product_reviews");
        var reviewWidgetEl = root.querySelector(".jdgm-review-widget");
        var revWidg = root.querySelector(".jdgm-rev-widg");
        var prevBadge = root.querySelector(".jdgm-prev-badge");
        var sectionDisplay = root.style ? root.style.display : "(no style)";

        var firstChild = revWidg ? revWidg.firstElementChild : null;
        var childCount = revWidg ? revWidg.children.length : 0;

        console.log("[JudgeMeSwap] post-init computed display:",
          "| section style.display:", sectionDisplay,
          "| #judgeme_product_reviews:", jdgWidg ? getComputedStyle(jdgWidg).display + "/vis:" + getComputedStyle(jdgWidg).visibility + "/h:" + jdgWidg.offsetHeight + "px" : "(missing)",
          "| .jdgm-review-widget classes:", reviewWidgetEl ? reviewWidgetEl.className : "(missing)",
          "| .jdgm-rev-widg:", revWidg ? getComputedStyle(revWidg).display + "/vis:" + getComputedStyle(revWidg).visibility + "/h:" + revWidg.offsetHeight + "px" + "/inline:" + (revWidg.style.display || "(none)") + "/children:" + childCount : "(missing)",
          "| .jdgm-prev-badge:", prevBadge ? getComputedStyle(prevBadge).display + "/h:" + prevBadge.offsetHeight + "px" : "(missing)"
        );

        // Enumerate all children with computed style + scrollHeight + content
        // size. scrollHeight = 0 reveals empty children (content stripped).
        // innerHTML length confirms whether server HTML survived our pipeline.
        if (revWidg) {
          Array.prototype.slice.call(revWidg.children).forEach(function (child, idx) {
            var cs = getComputedStyle(child);
            console.log("[JudgeMeSwap]   child[" + idx + "]:",
              child.tagName + "." + (child.className || "(no class)"),
              "disp:" + cs.display,
              "h(cs):" + cs.height,
              "minH:" + cs.minHeight,
              "h(off):" + child.offsetHeight + "px",
              "scrollH:" + child.scrollHeight + "px",
              "maxH:" + cs.maxHeight,
              "opac:" + cs.opacity,
              "vis:" + cs.visibility,
              "overflow:" + cs.overflow,
              "innerHTML.len:" + (child.innerHTML ? child.innerHTML.length : 0),
              "children:" + child.children.length);
          });
        }

        // Dump `.jdgm-rev-widg__body` innerHTML preview to confirm whether
        // review items survived. Server-rendered HTML had `.jdgm-rev` content;
        // if body innerHTML now empty/short, content was stripped.
        var bodyEl = root.querySelector(".jdgm-rev-widg__body");
        if (bodyEl) {
          var bodyHtml = bodyEl.innerHTML || "";
          console.log("[JudgeMeSwap] body innerHTML length:", bodyHtml.length,
            "| preview:", bodyHtml.substring(0, 200));
        }

        // Preview badge diagnostic — log each `.jdgm-prev-badge` globally
        // (badges live OUTSIDE the apps section, near product price). Reports
        // data attrs + computed display + offsetHeight + innerHTML preview +
        // parent chain (3 levels) to find what hides badge.
        var allBadges = document.querySelectorAll(".jdgm-prev-badge");
        console.log("[JudgeMeSwap] preview badges total:", allBadges.length);
        allBadges.forEach(function (b, i) {
          var bcs = getComputedStyle(b);
          var p1 = b.parentElement;
          var p2 = p1 ? p1.parentElement : null;
          console.log("[JudgeMeSwap]   badge[" + i + "]",
            "data-id:" + b.getAttribute("data-id"),
            "data-rating:" + b.getAttribute("data-average-rating"),
            "data-num:" + b.getAttribute("data-number-of-reviews"),
            "disp:" + bcs.display,
            "h:" + b.offsetHeight + "px",
            "innerHTML.len:" + (b.innerHTML ? b.innerHTML.length : 0),
            "parent:", p1 ? p1.tagName + "." + (p1.className || "").substring(0, 60) + " disp:" + getComputedStyle(p1).display + " h:" + p1.offsetHeight + "px" : "(none)",
            "gparent:", p2 ? p2.tagName + "." + (p2.className || "").substring(0, 60) + " disp:" + getComputedStyle(p2).display + " h:" + p2.offsetHeight + "px" : "(none)");
        });

        // Walk ancestor chain from `.jdgm-rev-widg` up to body — find which
        // ancestor collapses height. Content present + h(off)=0 = ancestor
        // applies `display:none`, `content-visibility:hidden`, or `height:0`.
        if (revWidg) {
          var ancestor = revWidg;
          var depth = 0;
          while (ancestor && ancestor !== document.body && depth < 12) {
            var acs = getComputedStyle(ancestor);
            console.log("[JudgeMeSwap] ancestor[" + depth + "]:",
              ancestor.tagName + (ancestor.id ? "#" + ancestor.id : "") + "." + (ancestor.className || "(no class)").substring(0, 80),
              "disp:" + acs.display,
              "h:" + ancestor.offsetHeight + "px",
              "cv:" + (acs.contentVisibility || "(n/a)"),
              "contain:" + (acs.contain || "(n/a)"),
              "vis:" + acs.visibility,
              "ovrflw:" + acs.overflow);
            ancestor = ancestor.parentElement;
            depth++;
          }
        }
      } catch (e) {
        console.warn("[JudgeMeSwap] diagnostic log threw:", e);
      }
    }
  }

  /**
   * Find the main product section id (e.g. "template--XXX__main").
   * Returns string or null. Used to fetch fresh `.jdgm-preview-badge` HTML
   * for the swapped-in product (badges live in main section, not apps).
   */
  function findMainSectionId() {
    var mp = document.querySelector(".m-main-product[data-section-id]");
    if (mp && mp.dataset.sectionId) return mp.dataset.sectionId;
    var fallback = document.querySelector("[id^='shopify-section-template--'][id*='__main']");
    if (fallback) return fallback.id.replace(/^shopify-section-/, "");
    return null;
  }

  /**
   * Refresh `.jdgm-preview-badge` star elements (rendered server-side via
   * `{{ product.metafields.judgeme.badge }}` in main-product-blocks.liquid:115
   * + mm-judgeme-widgets.liquid). Badges sit OUTSIDE `.m-app-section`, so the
   * apps-section innerHTML swap done by `update()` leaves them stale.
   *
   * Pure DOM swap — caller supplies `fetchedDom` (main section). Copies
   * data-* + style attrs from fetched badges onto live badges by index, then
   * asks the bundle to re-render stars via `jdgm.customizeBadges()`.
   */
  function swapPreviewBadgesFromDom(fetchedDom) {
    if (!fetchedDom) return;

    var fetchedBadges = fetchedDom.querySelectorAll(".jdgm-widget.jdgm-preview-badge");
    var liveBadges = document.querySelectorAll(".jdgm-widget.jdgm-preview-badge");
    var count = Math.min(fetchedBadges.length, liveBadges.length);

    DEBUG && console.log("[JudgeMeSwap:badges] fetched:", fetchedBadges.length,
      "live:", liveBadges.length, "→ updating:", count);

    for (var i = 0; i < count; i++) {
      var live = liveBadges[i];
      var fetched = fetchedBadges[i];

      // Copy data-* + style from fetched. `data-id` carries new product id;
      // `data-average-rating` / `data-number-of-reviews` set on inner
      // `.jdgm-prev-badge` (part of metafield HTML below).
      Array.prototype.forEach.call(fetched.attributes, function (attr) {
        if (attr.name.indexOf("data-") === 0 || attr.name === "style") {
          live.setAttribute(attr.name, attr.value);
        }
      });

      live.classList.remove("jdgm--done-setup");
      live.innerHTML = fetched.innerHTML;

      DEBUG && console.log("[JudgeMeSwap:badges]   badge[" + i + "] data-id →",
        fetched.getAttribute("data-id"),
        "innerHTML.len:", fetched.innerHTML.length);
    }

    if (window.jdgm) {
      try {
        if (window.jdgm.caches && window.jdgm.jQuery) {
          window.jdgm.caches.$prevBadges = window.jdgm.jQuery(".jdgm-preview-badge");
        }
        if (typeof window.jdgm.customizeBadges === "function") {
          window.jdgm.customizeBadges();
          DEBUG && console.log("[JudgeMeSwap:badges] customizeBadges() invoked");
        }
      } catch (e) {
        DEBUG && console.warn("[JudgeMeSwap:badges] customize threw:", e);
      }
    }
  }

  /**
   * Fetch-wrapper variant of `swapPreviewBadgesFromDom` for callers that
   * don't already have the main section DOM (e.g. standalone badge refresh).
   * `update(handle)` does NOT use this — it parallelizes a single fetch.
   */
  function updatePreviewBadges(handle) {
    if (typeof fetchSection !== "function") return Promise.resolve();

    var mainSectionId = findMainSectionId();
    if (!mainSectionId) return Promise.resolve();

    var baseUrl = (window.MinimogSettings && window.MinimogSettings.base_url) || "/";
    var fetchUrl = baseUrl + "products/" + handle;
    fetchUrl += (fetchUrl.indexOf("?") > -1 ? "&" : "?") + "_t=" + Date.now();

    return fetchSection(mainSectionId, { url: fetchUrl })
      .then(swapPreviewBadgesFromDom)
      .catch(function (err) {
        DEBUG && console.warn("[JudgeMeSwap:badges] failed:", err);
      });
  }

  /**
   * Fetch apps section + main section IN PARALLEL, swap apps content + reinit
   * widget, swap preview badges. Two concurrent network calls — total wall
   * time = max(apps, main), not sum.
   */
  function update(handle) {
    var info = findAppsSection();
    if (!info) return Promise.resolve();

    if (typeof fetchSection !== "function") {
      DEBUG && console.warn("[JudgeMeSwap] fetchSection unavailable (theme-global.js missing?)");
      return Promise.resolve();
    }

    var baseUrl = (window.MinimogSettings && window.MinimogSettings.base_url) || "/";
    var fetchUrl = baseUrl + "products/" + handle;
    fetchUrl += (fetchUrl.indexOf("?") > -1 ? "&" : "?") + "_t=" + Date.now();

    var mainSectionId = findMainSectionId();

    DEBUG && console.log("[JudgeMeSwap] fetching apps section:", info.sectionId, "for handle:", handle);
    var appsPromise = fetchSection(info.sectionId, { url: fetchUrl });

    var mainPromise;
    if (mainSectionId) {
      DEBUG && console.log("[JudgeMeSwap:badges] fetching main section:", mainSectionId, "for handle:", handle);
      mainPromise = fetchSection(mainSectionId, { url: fetchUrl });
    } else {
      DEBUG && console.warn("[JudgeMeSwap:badges] could not resolve main section id");
      mainPromise = Promise.resolve(null);
    }

    var appsTask = appsPromise
      .then(function (fetchedDom) {
        var fetchedAppsSection = fetchedDom.querySelector(".m-app-section");
        if (!fetchedAppsSection) {
          DEBUG && console.warn("[JudgeMeSwap] no .m-app-section in fetched HTML");
          return;
        }

        var fetchedJdg = fetchedAppsSection.querySelector("#judgeme_product_reviews");
        DEBUG && console.log("[JudgeMeSwap] fetched apps for", handle,
          "| #judgeme_product_reviews found:", !!fetchedJdg,
          "| innerHTML length:", fetchedJdg ? fetchedJdg.innerHTML.length : 0);

        info.appsSection.innerHTML = fetchedAppsSection.innerHTML;

        var liveJdg = info.appsSection.querySelector("#judgeme_product_reviews");
        var detection = detectReviews(liveJdg);

        // Force visibility via inline style. CSS `:has()` rules in
        // snippets/functional-fixes.liquid don't re-evaluate reliably across
        // browsers after innerHTML mutation, so inline `block` / `none` wins.
        info.appsSection.style.display = detection.hasReviews ? "block" : "none";

        DEBUG && console.log("[JudgeMeSwap] replaced apps content for", handle,
          "| has reviews:", detection.hasReviews,
          "| data-number-of-reviews:", detection.numReviewsAttr != null ? detection.numReviewsAttr : "(no .jdgm-rev-widg)",
          "| .jdgm-rev count:", detection.revCount,
          "| display:", info.appsSection.style.display);

        if (detection.hasReviews) reinitJudgeMe(info.appsSection);
      })
      .catch(function (err) {
        DEBUG && console.warn("[JudgeMeSwap] apps swap failed:", err);
      });

    var badgesTask = mainPromise
      .then(function (fetchedDom) {
        if (fetchedDom) swapPreviewBadgesFromDom(fetchedDom);
      })
      .catch(function (err) {
        DEBUG && console.warn("[JudgeMeSwap] badge swap failed:", err);
      });

    return Promise.all([appsTask, badgesTask]);
  }

  window.JudgeMeSwap = {
    update: update,
    updatePreviewBadges: updatePreviewBadges,
    swapPreviewBadgesFromDom: swapPreviewBadgesFromDom,
    findAppsSection: findAppsSection,
    findMainSectionId: findMainSectionId,
    detectReviews: detectReviews,
    reinitJudgeMe: reinitJudgeMe
  };

  DEBUG && console.log("[JudgeMeSwap] loaded");
})();
